import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, PracticeSession } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  BookOpen,
  Target,
  Clock,
  CheckCircle,
  Send,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface TechniqueProgress {
  techniqueId: string;
  problemsAttempted: number;
  problemsCorrect: number;
  accuracy: number;
  meetsRequirements: boolean;
}

export default function AssignmentPractice() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<any>(null);
  const [techniques, setTechniques] = useState<any[]>([]);
  const [sessions, setSessions] = useState<PracticeSession[]>([]);
  const [techniqueProgress, setTechniqueProgress] = useState<
    Map<string, TechniqueProgress>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    loadAssignment();
  }, [assignmentId]);

  useEffect(() => {
    if (assignment && techniques.length > 0) {
      loadAssignmentProgress();
    }
  }, [assignment, techniques]);

  const loadAssignment = async () => {
    try {
      // Get student's assignments to find this one
      const { assignments } = await api.getStudentAssignments();
      const found = assignments.find((a: any) => a.id === assignmentId);

      if (!found) {
        setError("Assignment not found");
        return;
      }

      setAssignment(found);

      // Load technique details
      const techniqueIds = found.content?.technique_ids || [];
      if (techniqueIds.length > 0) {
        await loadTechniqueDetails(techniqueIds);
      }
    } catch (error) {
      console.error("Failed to load assignment:", error);
      setError("Failed to load assignment");
    } finally {
      setLoading(false);
    }
  };

  const loadTechniqueDetails = async (techniqueIds: string[]) => {
    try {
      const { supabase } = await import("../lib/supabase");
      const { data, error } = await supabase
        .from("techniques")
        .select("id, name, category, difficulty")
        .in("id", techniqueIds);

      if (error) {
        console.error("Failed to load techniques:", error);
        return;
      }

      setTechniques(data || []);
    } catch (error) {
      console.error("Error loading technique details:", error);
    }
  };

  const loadAssignmentProgress = async () => {
    if (!assignmentId) return;

    try {
      // Load all practice sessions for this assignment
      const assignmentSessions = await api.getAssignmentSessions(assignmentId);
      setSessions(assignmentSessions);

      // Calculate progress per technique
      const techniqueIds = assignment.content?.technique_ids || [];
      const progressMap = new Map<string, TechniqueProgress>();

      techniqueIds.forEach((techniqueId: string) => {
        // Filter sessions for this technique
        const techniqueSessions = assignmentSessions.filter(
          (s) => s.technique_id === techniqueId && s.completed_at,
        );

        // Aggregate stats
        const totalAttempted = techniqueSessions.reduce(
          (sum, s) => sum + (s.problems_attempted || 0),
          0,
        );
        const totalCorrect = techniqueSessions.reduce(
          (sum, s) => sum + (s.problems_correct || 0),
          0,
        );
        const accuracy =
          totalAttempted > 0 ? (totalCorrect / totalAttempted) * 100 : 0;

        const minProblems = assignment.content?.min_problems || 20;
        const minAccuracy = assignment.content?.min_accuracy || 80;
        const meetsRequirements =
          totalAttempted >= minProblems && accuracy >= minAccuracy;

        progressMap.set(techniqueId, {
          techniqueId,
          problemsAttempted: totalAttempted,
          problemsCorrect: totalCorrect,
          accuracy,
          meetsRequirements,
        });
      });

      setTechniqueProgress(progressMap);
    } catch (error) {
      console.error("Failed to load assignment progress:", error);
    }
  };

  const handleStartTechnique = (techniqueId: string) => {
    // Navigate to practice page for this technique
    navigate(`/practice?technique=${techniqueId}&assignment=${assignmentId}`);
  };

  const handleSubmitAssignment = async () => {
    if (!assignmentId) return;

    setSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      // Aggregate work data from all sessions
      const sessionIds = sessions
        .filter((s) => s.completed_at)
        .map((s) => s.id);

      const totalAttempted = sessions.reduce(
        (sum, s) => sum + (s.problems_attempted || 0),
        0,
      );
      const totalCorrect = sessions.reduce(
        (sum, s) => sum + (s.problems_correct || 0),
        0,
      );

      const workData = {
        session_ids: sessionIds,
        problems_attempted: totalAttempted,
        problems_correct: totalCorrect,
      };

      // Submit the assignment
      const result = await api.submitAssignment(assignmentId, workData, true);

      setSuccessMessage(result.message || "Assignment submitted successfully!");

      // Reload assignment to get updated status
      setTimeout(() => {
        loadAssignment();
        setSuccessMessage("");
      }, 2000);
    } catch (error: any) {
      console.error("Failed to submit assignment:", error);
      setError(error.message || "Failed to submit assignment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p>Loading assignment...</p>
      </div>
    );
  }

  if (error && !assignment) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>{error || "Assignment not found"}</AlertDescription>
        </Alert>
        <Button className="mt-4" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const techniqueIds = assignment.content?.technique_ids || [];
  const minProblems = assignment.content?.min_problems || 20;
  const minAccuracy = assignment.content?.min_accuracy || 80;
  const submission = assignment.my_submission?.[0];

  // Check if all requirements are met
  const allRequirementsMet = techniqueIds.every((techniqueId: string) => {
    const progress = techniqueProgress.get(techniqueId);
    return progress?.meetsRequirements || false;
  });

  const canSubmit =
    allRequirementsMet &&
    submission?.status !== "submitted" &&
    submission?.status !== "graded";

  return (
    <div className="container mx-auto px-4 py-8">
      <Button
        variant="ghost"
        onClick={() => navigate("/dashboard")}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>

      {successMessage && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-800">
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-2xl">{assignment.title}</CardTitle>
          <CardDescription>
            {assignment.classrooms?.name} • {assignment.points_possible} points
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {assignment.description && (
              <div>
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-gray-700">{assignment.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <Target className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Min Problems</p>
                  <p className="font-semibold">{minProblems}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm text-gray-600">Min Accuracy</p>
                  <p className="font-semibold">{minAccuracy}%</p>
                </div>
              </div>
              {assignment.due_date && (
                <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-600">Due Date</p>
                    <p className="font-semibold">
                      {new Date(assignment.due_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {submission && (
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription>
                  <strong>Status:</strong>{" "}
                  {submission.status === "graded"
                    ? "Graded"
                    : submission.status === "submitted"
                      ? "Submitted"
                      : "In Progress"}
                  {submission.score !== null &&
                    submission.score !== undefined &&
                    ` • Score: ${submission.score}%`}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Techniques to Practice
          </CardTitle>
          <CardDescription>
            Practice each technique to complete this assignment
          </CardDescription>
        </CardHeader>
        <CardContent>
          {techniqueIds.length === 0 ? (
            <p className="text-gray-500">No techniques assigned</p>
          ) : (
            <div className="space-y-3">
              {techniqueIds.map((techniqueId: string) => {
                const technique = techniques.find((t) => t.id === techniqueId);
                const progress = techniqueProgress.get(techniqueId);
                const progressPercent = progress
                  ? Math.min(
                      (progress.problemsAttempted / minProblems) * 100,
                      100,
                    )
                  : 0;

                return (
                  <div
                    key={techniqueId}
                    className="p-4 border rounded-lg hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <p className="font-medium">
                          {technique?.name || techniqueId}
                        </p>
                        {technique && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                              {technique.category.replace(/_/g, " ")}
                            </span>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {technique.difficulty}
                            </span>
                          </div>
                        )}
                      </div>
                      <Button onClick={() => handleStartTechnique(techniqueId)}>
                        Practice
                      </Button>
                    </div>

                    {progress && progress.problemsAttempted > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-4">
                            <span className="text-gray-600">
                              {progress.problemsAttempted}/{minProblems}{" "}
                              problems
                            </span>
                            <span className="text-gray-600">
                              {progress.accuracy.toFixed(1)}% accuracy
                            </span>
                          </div>
                          {progress.meetsRequirements && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        <Progress value={progressPercent} className="h-2" />
                      </div>
                    )}

                    {(!progress || progress.problemsAttempted === 0) && (
                      <div className="text-sm text-gray-500">
                        Not started yet
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {techniqueIds.length > 0 && (
            <div className="mt-6 space-y-4">
              <Alert>
                <TrendingUp className="w-4 h-4" />
                <AlertDescription>
                  Complete {minProblems} problems with at least {minAccuracy}%
                  accuracy for each technique
                </AlertDescription>
              </Alert>

              {!canSubmit && !allRequirementsMet && (
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    Complete all technique requirements to submit this
                    assignment
                  </AlertDescription>
                </Alert>
              )}

              {canSubmit && (
                <Button
                  onClick={handleSubmitAssignment}
                  disabled={submitting}
                  className="w-full"
                  size="lg"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? "Submitting..." : "Submit Assignment"}
                </Button>
              )}

              {(submission?.status === "submitted" ||
                submission?.status === "graded") && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Assignment already submitted
                    {submission.status === "graded" &&
                      submission.score !== null &&
                      submission.score !== undefined &&
                      ` • Score: ${submission.score}%`}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
